import { useEffect, useState } from 'react'
import { submitPlanEmail, type EmailCaptureSource } from '../lib/api'
import { trackAdsConversionOnce } from '../lib/google-ads'
import {
  decidePlanCtaSurface,
  planWhatsAppHref,
  planClickBeaconUrl,
  whatsAppConfigured,
  isPhoneLikeUserAgent,
} from './planCtaSurface'
import { WhatsAppQr } from './WhatsAppQr'
import { planTargetHero } from '../lib/planTarget'
import type { HonestPlan } from '../lib/types'
import { getStrings, intlLocale, type Lang } from '../i18n'

// The conversion surface of the funnel. What renders is gated by whether
// VITE_WHATSAPP_NUMBER is baked into the build (whatsAppConfigured):
//
//   * Number UNSET (dark-ship control / dev / tests) — the legacy design:
//       US + token   → SMS-primary card (UsCta) with a collapsed email toggle.
//       everyone else → email capture form. The FORM copy is the 17.9%
//                       conversion control; don't touch it without an A/B
//                       plan. (The shared lede/body pitch above the form was
//                       repitched 2026-07-31 to the measured-TDEE framing —
//                       same substance on every surface — so the 17.9% figure
//                       predates that copy.)
//     Both email paths POST to /api/plan/email; the `source` field
//     distinguishes them so we can A/B-compare conversion.
//
//   * Number SET (live design, prod) — email is gone; the CTA is a messaging
//     handoff. The plan already exists and is theirs: one prefilled message
//     claims it and the personalized plan image lands in the same thread about a
//     minute later. The preview bubble is an honest mock of that real
//     deliverable (plan image + real timeline), not an overpromise — texting an
//     unknown number is a black box; the preview makes it a known transaction.
//     First-person possession copy is kept from the 2026-06-02 investigation
//     (5.4% US vs 17.9% intl traced to the old "message your coach" relationship
//     ask — analysis/2026-06-02-us-cta-investigation-zh.md).
//       US + token (UsLiveCard)   → SMS is PRIMARY (filled button), WhatsApp a
//         secondary text link beneath it. 2026-07-30 flip: making WhatsApp the
//         primary (2026-07-22..29) halved US paid-segment click-through
//         (45% → 20%, z=2.43 p≈0.008), so SMS is the push again. Desktop is the
//         exception — sms: has no handler there (0 desktop redemptions in 56
//         impressions across both eras) — so it keeps the WhatsApp QR, the one
//         path that can move the claim onto a phone.
//       everyone else + token (IntlWhatsAppCta) → WhatsApp-only CTA (915 is a
//         US A2P number, so SMS stays US-only).
//       number set but NO token   → nothing to claim, email gone → render
//         nothing.

interface Props {
  conversationId: string | null
  handoffToken: string | null
  country: string | null
  tdeeRecommended: number | null
  // The calc's own TDEE band (tdee_low..tdee_high), for the measured-range
  // reveal on the lose-intent US cards: the same honest band the result card
  // drew, redrawn as the thing the formula can't resolve. Null/absent on
  // legacy sessions — the lede degrades to the numberless variant, no bar.
  tdeeLow?: number | null
  tdeeHigh?: number | null
  // Under-18 gate (defense-in-depth). The server never emits a calc payload
  // for a minor, so this card never even mounts for one — but if a minor
  // profile ever reaches here through any future path, render nothing: no SMS
  // claim, no email capture, no conversion surface for an under-18 user.
  isMinor?: boolean
  // True when this is a re-run by a user who already clicked the SMS claim link
  // on a prior calc — i.e. they're likely already texting their NanoRhino
  // coach. US-only: surfaces a "text UPDATE to refresh your plan" hint so a
  // returning user can refresh an existing coach instead of re-claiming. Never
  // shown to first-time users. Absent/false for first-timers.
  hasExistingHandoff?: boolean
  // True when the user reported a GLP-1 medication. On the US SMS surface this
  // swaps the measured-deficit pitch (real TDEE measured from logs, weekly
  // retune) for the protein/muscle-preservation framing this cohort responds
  // to — the shot already handles appetite; the coach's value is measuring
  // what they actually eat, keeping the loss muscle-safe, and hitting protein
  // on low appetite (research/glp1-us-cohort.md). On the plan-image preview
  // it also swaps the calorie hero for the numberless protein-floor framing
  // the real GLP-1 card uses — a raw kcal here would misrepresent that
  // cohort's card.
  isGlp1?: boolean
  // Goal intent from the calc payload. Drives the honest DEFICIT-target band
  // shown in the preview hero (lose/recomp/gain/maintain use different bands;
  // see lib/planTarget.ts) AND gates the measured-loop body copy: the weekly
  // measured-TDEE retune promise is LOSE-ONLY (the deficit loop's eligibility
  // gate is goal=lose), so maintain/gain/recomp get the target+protein+habits
  // body instead. Optional — defaults to 'lose' when absent.
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  // Honest-rate floor result from the calc payload (lose intent only, 2026-07
  // contract). When mode='adjusted' the preview hero shows the clamped floor
  // target and the card carries the one-line honest-pace note; when
  // mode='activity_first' the lede + hero swap to the eating-anchor/movement
  // framing (no calorie-cutting date exists to promise). Null/absent = legacy
  // band behavior.
  honestPlan?: HonestPlan | null
  // UI locale (en default, es on /tdee/es/).
  lang?: Lang
  // Admin session-replay override (web/src/admin/SessionDetail.tsx): forces
  // the phone/desktop action stack instead of sniffing THIS browser, so a
  // desktop admin can see exactly what a mobile user saw. Never set on the
  // real funnel surfaces — absent means sniff as always.
  forceDevice?: 'phone' | 'desktop'
}

const PHONE_TEL = '+19152777888'
const API_BASE = import.meta.env.DEV ? '' : import.meta.env.BASE_URL.replace(/\/$/, '')

type EmailStatus = 'idle' | 'submitting' | 'submitted' | 'error'

// Fire-and-forget click beacon. `channel` routes the server-side stamp:
// omitted/default → sms_link_clicked_at (byte-identical to before); 'whatsapp'
// → wa_link_clicked_at. URL built by the pure planClickBeaconUrl so the routing
// is unit-tested without a DOM.
/**
 * True when this device can actually complete a `wa.me` / `sms:` handoff, i.e.
 * it is a phone. Desktop gets a QR instead (see WhatsAppQr for the numbers).
 *
 * Starts at `true` and only flips on mount: the first paint therefore renders
 * the button surface, which is both today's behaviour and the correct answer
 * for ~80% of traffic. A desktop visitor sees the QR swap in on the same tick
 * as hydration. Reading navigator during render would be a hydration hazard for
 * no benefit.
 */
function usePhoneLikeDevice(): boolean {
  const [phoneLike, setPhoneLike] = useState(true)
  useEffect(() => {
    if (typeof navigator === 'undefined') return
    setPhoneLike(isPhoneLikeUserAgent(navigator.userAgent, navigator.maxTouchPoints))
  }, [])
  return phoneLike
}

function recordCtaClick(handoffToken: string, channel?: 'whatsapp') {
  const url = planClickBeaconUrl({ apiBase: API_BASE, handoffToken, channel })
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(url)
  } else {
    fetch(url, { method: 'POST' }).catch(() => {})
  }
}

export function PlanCtaCard({
  conversationId,
  handoffToken,
  country,
  tdeeRecommended,
  tdeeLow,
  tdeeHigh,
  isMinor,
  hasExistingHandoff,
  isGlp1,
  intent,
  honestPlan,
  lang = 'en',
  forceDevice,
}: Props) {
  const t = getStrings(lang)
  // Honest-rate floor: lose-intent user whose maintenance sits at their
  // safety floor — the default measured-loop lede is replaced by the
  // eating-anchor + movement lede (server-computed; see lib/types.ts).
  const activityFirst = !isGlp1 && honestPlan?.mode === 'activity_first'
  // Weekly measured-TDEE retune promise is LOSE-ONLY (deficit-loop
  // eligibility gate): maintain/gain/recomp see the target+protein+habits
  // body instead of the weekly-recalc pitch.
  const loopIntent = (intent ?? 'lose') === 'lose'
  // Debug-only: ?sms=1 forces the US SMS CTA branch regardless of geo, so the
  // SMS→EC2-coach kickoff flow can be tested on prod from a non-US IP. Real
  // users never append it, so the intl email control (17.9%) is untouched.
  const forceSms =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('sms')
  // Surface + returning-user-hint decision lives in a pure, unit-tested helper.
  const { surface, showRehandoffHint } = decidePlanCtaSurface({
    country,
    handoffToken,
    isMinor,
    hasExistingHandoff,
    forceSms,
  })
  // Under-18 gate: never show any conversion surface to a minor. This can't
  // normally be reached (the server skips the calc/CTA path for minors), so
  // it's a belt-and-suspenders guard.
  if (surface === 'none') return null
  // First-person prefill, not carrier opt-in grammar. The old body
  // (`START Y3SKB58P`) read as marketing-SMS subscription language — all-caps
  // keyword + promo-looking code — which the 2026-06-02 CTA investigation
  // flagged as a friction source. The token is a profile slug
  // (m-5ft10-176lb-6k-0604) minted server-side, so the message reads as the
  // user describing themselves. Claim semantics ("send me my plan",
  // 2026-06-10): the plan was never shown on the web, so the text is how the
  // user collects it. Deliberately numberless: the prefill used to quote
  // tdee_recommended, but the plan card delivers the DEFICIT band — quoting
  // the higher TDEE number here created a "where did my 2,010 go?"
  // mismatch. The number reveal belongs to the MMS card (preview shows it
  // blurred). EC2 coach parses the slug from anywhere in the message
  // (legacy `START <token>` still accepted). Do NOT reintroduce
  // `START <token>` grammar — measured conversion killer.
  const smsHref = handoffToken
    ? `sms:${PHONE_TEL}?&body=${encodeURIComponent(
        t.cta.smsPrefill(handoffToken.toLowerCase())
      )}`
    : null

  // WhatsApp deep link (dark-shipped). Non-null ONLY when VITE_WHATSAPP_NUMBER
  // is configured AND a handoff token exists — that non-null return is the
  // "WhatsApp is the primary CTA" signal for BOTH surfaces (US + intl). The
  // prefill is the SAME first-person sentence the SMS CTA uses (token
  // lowercased): the 2026-06-02 finding was that all-caps keyword grammar reads
  // as carrier spam, and the WhatsApp channel parses the slug from anywhere in
  // the sentence, so `START <token>` is gone. When unset this is null and both
  // surfaces render their current control unchanged.
  const waHref = planWhatsAppHref({
    surface,
    whatsappNumber: import.meta.env.VITE_WHATSAPP_NUMBER,
    handoffToken,
    prefill: handoffToken ? t.cta.smsPrefill(handoffToken.toLowerCase()) : '',
  })
  // "WhatsApp is live" (number baked in), independent of whether THIS session
  // has a token. Distinguishes waHref===null "no number" (dark-ship control:
  // keep email) from waHref===null "no token but number set" (live design: email
  // is removed → render nothing rather than fall back to an email form).
  const waConfigured = whatsAppConfigured(import.meta.env.VITE_WHATSAPP_NUMBER)

  // US live surface (VITE_WHATSAPP_NUMBER set): SMS is the primary claim button,
  // WhatsApp a secondary text link beneath it, plus the coach thread below
  // (plan drop → meal logged → Sunday re-measure). No email on this
  // surface. Gated on waHref so with the number unset we fall through to the
  // byte-identical legacy US SMS card below (dark-ship invariant).
  if (surface === 'us_sms' && smsHref && waHref) {
    return (
      <UsLiveCard
        waHref={waHref}
        smsHref={smsHref}
        handoffToken={handoffToken!}
        tdeeRecommended={tdeeRecommended}
        tdeeLow={tdeeLow}
        tdeeHigh={tdeeHigh}
        isGlp1={isGlp1}
        activityFirst={activityFirst}
        intent={intent}
        honestPlan={honestPlan}
        showRehandoffHint={showRehandoffHint}
        lang={lang}
        forceDevice={forceDevice}
      />
    )
  }

  if (surface === 'us_sms' && smsHref) {
    return (
      // cta-card-enter: subtle fade + slide-up on mount (App.tsx holds the
      // card back ~500ms after the bridge text commits, so the entrance
      // reads as the next beat of the conversation, not a popup). US branch
      // only — the intl email card renders instantly, unchanged. Disabled
      // under prefers-reduced-motion in CSS.
      <div className="cta-card cta-card-enter">
        <header className="cta-card-header">
          <span className="kicker">
            {isGlp1 ? t.cta.kickerReadyGlp1 : t.cta.kickerReady}
          </span>
        </header>

        {isGlp1 ? (
          /* GLP-1: the shot already drives the deficit — the pitch is
             protecting muscle and hitting protein on low appetite, not a
             goal-date countdown (research/glp1-us-cohort.md). */
          <Glp1Narrative lang={lang} />
        ) : (
          <UsNarrative
            loopIntent={loopIntent}
            activityFirst={activityFirst}
            tdeeRecommended={tdeeRecommended}
            tdeeLow={tdeeLow}
            tdeeHigh={tdeeHigh}
            lang={lang}
          />
        )}

        <NoAppLine lang={lang} />

        <UsCta
          smsHref={smsHref}
          handoffToken={handoffToken!}
          conversationId={conversationId}
          tdeeRecommended={tdeeRecommended}
          intent={intent}
          isGlp1={isGlp1}
          honestPlan={honestPlan}
          lang={lang}
        />

        {/* Returning-user hint: only when this conversation already clicked the
            SMS claim link on a prior calc — i.e. they're likely already texting
            their coach. A re-run shouldn't pitch a first-time link to someone
            who's linked; it should let them refresh the existing coach with one
            text. US branch only (this is the SMS surface); first-timers
            (hasExistingHandoff false) never see it. */}
        {showRehandoffHint &&
          (lang === 'es' ? (
            <p className="cta-card-rehandoff">
              ¿Ya le escribes a tu coach de <strong>NanoRhino</strong>? Envía{' '}
              <strong>UPDATE</strong> al (915) 277-7888 para actualizar tu plan
              con estos números.
            </p>
          ) : (
            <p className="cta-card-rehandoff">
              Already texting your <strong>NanoRhino</strong> coach? Text{' '}
              <strong>UPDATE</strong> to (915) 277-7888 to refresh your plan with
              these numbers.
            </p>
          ))}
      </div>
    )
  }

  // Non-US branch.
  //   * Number configured + token → WhatsApp-only CTA (email removed in the
  //     live design; SMS is US-only because 915 is a US A2P number).
  //   * Number configured but NO token (edge: token minting failed, or a US
  //     user with no token who fell through to this surface) → render nothing:
  //     there's no plan to claim over WhatsApp and email is gone in the live
  //     design.
  //   * Number UNSET (dark-ship control / dev / tests) → the email capture form,
  //     UNCHANGED (the form itself is the 17.9% conversion control; the
  //     lede/body pitch above it carries the 2026-07-31 measured-TDEE framing,
  //     English and Spanish parallel).
  if (waHref && handoffToken) {
    return (
      <div className="cta-card">
        <ClaimPitch
          handoffToken={handoffToken}
          isGlp1={isGlp1}
          activityFirst={activityFirst}
          intent={intent}
          lang={lang}
        />
        <IntlWhatsAppCta
          waHref={waHref}
          handoffToken={handoffToken}
          lang={lang}
          forceDevice={forceDevice}
        />
        <ClaimFineprint
          showResults={showResultsClaim({ intent, isGlp1, honestPlan })}
          lang={lang}
        />
      </div>
    )
  }

  // Live design, no token to claim → no email fallback; render nothing.
  if (waConfigured) return null

  // Legacy control (number unset): email capture form, unchanged.
  return (
    <div className="cta-card">
      <header className="cta-card-header">
        <span className="kicker">{t.cta.intlKicker}</span>
      </header>

      <p className="cta-card-lede">{loopIntent ? t.cta.intlLede : t.cta.intlLedeSteady}</p>

      {/* Weekly-retune promise is LOSE-ONLY: maintain/gain/recomp get the
          target+protein+habits body. */}
      {loopIntent ? (
        lang === 'es' ? (
          <p className="cta-card-body">
            <strong>NanoRhino</strong> es un coach de nutrición con IA que lo mide
            por ti: envía cada comida — una foto o unas palabras, el coach hace
            los cálculos — y tu meta diaria se recalcula cada semana con lo que
            realmente comes y pesas, para que el plan siga a tu cuerpo, no a una
            fórmula.
          </p>
        ) : (
          <p className="cta-card-body">
            <strong>NanoRhino</strong> is an AI nutrition coach that measures it
            for you: send each meal — a photo or a few words, the coach does the
            math — and your daily target is recalculated every week from what you
            actually eat and weigh, so the plan tracks your body, not a formula.
          </p>
        )
      ) : lang === 'es' ? (
        <p className="cta-card-body">
          <strong>NanoRhino</strong> es un coach de nutrición con IA que lo
          lleva contigo: envía cada comida — una foto o unas palabras, el coach
          hace los cálculos — y tu plan se mantiene en torno a tu meta diaria,
          tu proteína diaria y hábitos que sostienen la semana.
        </p>
      ) : (
        <p className="cta-card-body">
          <strong>NanoRhino</strong> is an AI nutrition coach that runs it with
          you: send each meal — a photo or a few words, the coach does the math
          — and your plan stays built around your daily target, your protein
          goal, and habits that hold the week steady.
        </p>
      )}

      <EmailCaptureForm conversationId={conversationId} source="intl_form" lang={lang} />
    </div>
  )
}

// Non-US WhatsApp CTA surface: the wa.me deep link is the ONLY action (email
// removed from the live design; SMS is US-only because 915 is a US A2P number).
// Rendered only when planWhatsAppHref returned a link, i.e. a number is
// configured and a handoff token exists.
function IntlWhatsAppCta({
  waHref,
  handoffToken,
  lang = 'en',
  forceDevice,
}: {
  waHref: string
  handoffToken: string
  lang?: Lang
  forceDevice?: 'phone' | 'desktop'
}) {
  const t = getStrings(lang)
  const phoneLikeAuto = usePhoneLikeDevice()
  const phoneLike = forceDevice ? forceDevice === 'phone' : phoneLikeAuto
  // Same desktop dead end as the US surface — wa.me is the ONLY action here, so
  // a desktop visitor with no QR has literally nothing that can work.
  if (!phoneLike) {
    return (
      <>
        <WhatsAppQr href={waHref} caption={t.cta.qrCaption} />
        <p className="cta-card-fineprint cta-card-mechanic">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              recordCtaClick(handoffToken, 'whatsapp')
              trackAdsConversionOnce('trial_started')
            }}
          >
            {t.cta.qrDesktopLink}
          </a>
        </p>
      </>
    )
  }
  return (
    <>
      <a
        className="cta-card-btn"
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          // WhatsApp tap → wa_link_clicked_at (channel=whatsapp), the same tap
          // proxy the US SMS link uses (a deep link can't confirm the send).
          recordCtaClick(handoffToken, 'whatsapp')
          // Ads funnel: a CTA tap = trial_started, consistent with the US link.
          trackAdsConversionOnce('trial_started')
        }}
      >
        {t.cta.claimBtnWhatsapp}
      </a>
      <p className="cta-card-fineprint cta-card-mechanic">{t.cta.whatsappMechanicHint}</p>
    </>
  )
}

// Measured-range reveal (2026-07-31 CTA redesign, lose-intent US cards).
// Redraws the calc's own tdee_low..tdee_high band — the same honest numbers
// the result card just showed, same .range-bar geometry for the visual echo —
// as the thing the formula CANNOT resolve: the dot is the formula's pick, the
// range is where the real burn hides, and the body copy sells logging as the
// instrument that pins it. Flips the old "your number isn't real" negging
// into a mystery with a two-week resolution.
function CtaRangeReveal({
  low,
  high,
  rec,
  lang = 'en',
}: {
  low: number
  high: number
  rec: number
  lang?: Lang
}) {
  const t = getStrings(lang)
  const loc = intlLocale(lang)
  const span = Math.max(high - low, 1)
  const pct = ((Math.min(Math.max(rec, low), high) - low) / span) * 100
  return (
    <div
      className="cta-range"
      role="img"
      aria-label={t.cta.rangeAria(
        low.toLocaleString(loc),
        high.toLocaleString(loc),
        rec.toLocaleString(loc)
      )}
    >
      <div className="range-bar">
        <div className="range-bar-track" />
        <div className="range-bar-tick" style={{ left: 0 }} />
        <div className="range-bar-tick" style={{ left: '100%' }} />
        <div className="range-bar-dot" style={{ left: `${pct}%` }} />
      </div>
      <div className="cta-range-labels">
        <span>{low.toLocaleString(loc)}</span>
        <span>{high.toLocaleString(loc)}</span>
      </div>
      <p className="cta-range-caption">{t.cta.rangeCaption}</p>
    </div>
  )
}

// Claim-card pitch (2026-10-02 redesign, live US + intl WhatsApp surfaces).
// Mirrors the onboarding Starting Plan the user receives after claiming, so
// the card previews the product rather than arguing about measurement:
//   1. Lose intent: "doing everything right and the scale won't move?" — the
//      calculator number was wrong (measured −500 Cal); the calorie
//      recognizer measures the real one. Other intents: formula-estimate lede.
//   No "free" anywhere (2026-10-02 owner call: recruit for results, not price).
//   2. AI coach + a human nutritionist who steps in on a plateau.
//   3. Member results, no shots or pills — lose intent only, never GLP-1
//      (they ARE on shots) and never activity-first (no cutting pace to
//      promise). The asterisked median is footnoted under the button.
// Weekly-retune wording stays LOSE-ONLY (deficit-loop eligibility gate):
// other intents get the "see what you really eat" recognizer body.
function showResultsClaim({
  intent,
  isGlp1,
  honestPlan,
}: {
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  isGlp1?: boolean
  honestPlan?: HonestPlan | null
}): boolean {
  return (intent ?? 'lose') === 'lose' && !isGlp1 && honestPlan?.mode !== 'activity_first'
}

// Median monthly loss in the user's own units: the profile slug carries kg
// for metric users (4 lb ≈ 1.8 kg).
function memberMedianLoss(handoffToken: string, lang: Lang): string {
  const metric = parseProfileSlug(handoffToken).weight?.endsWith('kg') ?? false
  return metric ? `${(1.8).toLocaleString(intlLocale(lang))} kg` : '4 lb'
}

function ClaimPitch({
  handoffToken,
  isGlp1,
  activityFirst,
  intent,
  lang = 'en',
}: {
  handoffToken: string
  isGlp1?: boolean
  activityFirst: boolean
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  lang?: Lang
}) {
  const t = getStrings(lang)
  const loopIntent = (intent ?? 'lose') === 'lose'
  // Same rule as showResultsClaim; activityFirst is already derived from
  // honestPlan by the caller.
  const showResults = loopIntent && !isGlp1 && !activityFirst
  return (
    <>
      <header className="cta-card-header">
        <span className="kicker">{t.cta.pitchKicker}</span>
      </header>
      {isGlp1 ? (
        /* GLP-1: protein/muscle framing, never a weekly-cadence promise
           (research/glp1-us-cohort.md). */
        <Glp1Narrative lang={lang} />
      ) : (
        <>
          {/* Lose intent speaks to the paying cohort's actual story ("I do
              everything right and the scale won't move") with the measured
              −500 Cal finding (9/14 deficit-loop report: measured burn vs the
              calculator number, median −511, 22/24 lower). Other intents and
              activity-first keep the formula-estimate framing. */}
          <p className="cta-card-headline">
            {loopIntent && !activityFirst ? t.cta.pitchHeadlineLose : t.cta.pitchHeadline}
          </p>
          <p className="cta-card-body">
            {activityFirst
              ? t.cta.ledeActivity
              : loopIntent
                ? t.cta.pitchSubLose
                : t.cta.ledeEstimate}
          </p>
        </>
      )}
      <ul className="cta-feats">
        <li>
          <span className="cta-feat-icon" aria-hidden="true">📸</span>
          <div>
            <strong>{t.cta.featRecognizerTitle}</strong>
            <p>{loopIntent && !isGlp1 ? t.cta.featRecognizerBodyLoop : t.cta.featRecognizerBodySteady}</p>
          </div>
        </li>
        <li>
          <span className="cta-feat-icon" aria-hidden="true">🧑‍⚕️</span>
          <div>
            <strong>{t.cta.featNutritionistTitle}</strong>
            <p>{t.cta.featNutritionistBody}</p>
          </div>
        </li>
        {showResults && (
          <li>
            <span className="cta-feat-icon" aria-hidden="true">📉</span>
            <div>
              <strong>{t.cta.featResultsTitle(memberMedianLoss(handoffToken, lang))}</strong>
              <p>{t.cta.featResultsBody}</p>
            </div>
          </li>
        )}
      </ul>
    </>
  )
}

function ClaimFineprint({ showResults, lang = 'en' }: { showResults: boolean; lang?: Lang }) {
  const t = getStrings(lang)
  return (
    <>
      <p className="cta-card-fineprint">{t.cta.fineprintClaim}</p>
      {showResults && <p className="cta-card-fineprint cta-card-footnote">{t.cta.resultsFootnote}</p>}
    </>
  )
}

// Lede + range + body narrative for the non-GLP-1 US cards. One component so
// the legacy dark-ship card and the live card can't drift apart. Lose intent
// gets the range reveal + one-sentence loop pitch; maintain/gain/recomp get
// the estimate lede + steady-plan body (the weekly measured-retune promise is
// LOSE-ONLY — deficit-loop eligibility gate); activity-first swaps the lede
// for the eating-anchor + movement framing and skips the bar (no cutting
// target exists to drift).
//
// 2026-07-31 (evening) cut: this was a lede + bar + TWO body paragraphs of
// measurement science — ~100 words of physiology before the reader learned
// what she'd have to DO. The cohort that actually redeems is 51 y/o, 89%
// female, ~206 lb (prod D1, 60d) and has quit three tracking apps; the
// error-bar argument indexes to the under-35 segment that redeems at 2.4%.
// The whole loop now lives in ONE sentence (t.cta.rangeBody) and the reader
// reaches the button in about a screen.
function UsNarrative({
  loopIntent,
  activityFirst,
  tdeeRecommended,
  tdeeLow,
  tdeeHigh,
  lang = 'en',
}: {
  loopIntent: boolean
  activityFirst: boolean
  tdeeRecommended: number | null
  tdeeLow?: number | null
  tdeeHigh?: number | null
  lang?: Lang
}) {
  const t = getStrings(lang)
  const loc = intlLocale(lang)
  const showRange =
    loopIntent &&
    !activityFirst &&
    tdeeRecommended != null &&
    tdeeLow != null &&
    tdeeHigh != null &&
    tdeeHigh > tdeeLow
  return (
    <>
      {/* rangeLede ends with a colon INTO the bar, so it renders only when
          the bar does; the numberless variant stands alone otherwise. */}
      <p className="cta-card-lede">
        {activityFirst
          ? t.cta.ledeActivity
          : !loopIntent
            ? t.cta.ledeEstimate
            : showRange
              ? t.cta.rangeLede(tdeeRecommended.toLocaleString(loc))
              : t.cta.rangeLedeNoNumber}
      </p>
      {showRange && (
        <CtaRangeReveal
          low={tdeeLow}
          high={tdeeHigh}
          rec={tdeeRecommended}
          lang={lang}
        />
      )}
      {/* Weekly-retune promise is LOSE-ONLY: maintain/gain/recomp get the
          steady-plan body instead. */}
      <p className="cta-card-body">{loopIntent ? t.cta.rangeBody : t.cta.bodySteady}</p>
    </>
  )
}

// GLP-1 narrative — protein frame only, never a weekly-cadence promise
// (research/glp1-us-cohort.md). Was two long paragraphs inlined TWICE (legacy
// card + live card, en/es each); now one component reading i18n, so the two
// cards cannot drift and the copy is half the length.
function Glp1Narrative({ lang = 'en' }: { lang?: Lang }) {
  const t = getStrings(lang)
  return (
    <>
      <p className="cta-card-lede">{t.cta.glp1Lede}</p>
      <p className="cta-card-body">{t.cta.glp1Body}</p>
    </>
  )
}

// The effort promise — "no app, no counting" — promoted 2026-07-31 from the
// bottom of the card (buried inside the aria-hidden demo) to the last line
// before the button. Coach-conversation mining says this is the magic moment
// for the cohort that actually converts ("you never made me count calories, I
// just tell you what I ate"), and prod redemption skews hard to 45+ women who
// will not install another tracker. Shown for EVERY cohort, GLP-1 included.
function NoAppLine({ lang = 'en' }: { lang?: Lang }) {
  const t = getStrings(lang)
  return (
    <p className="cta-card-body">
      <strong>{t.cta.noAppLead}</strong> {t.cta.noAppBody}
    </p>
  )
}

// Honest preview of the real deliverable: the plan IMAGE that actually lands
// in the thread ~1 minute after the claim text, replacing the 2026-06-02 fake
// instant-reply bubble — the preview must promise exactly what the pipeline
// delivers. Uses the real TDEE number when available so it reads as "my
// plan", not marketing. Pure presentation, no interactivity.
// Rendered as the first message of CoachThread (below the button) since
// 2026-07-31 — it used to sit above the button, which pushed the tap ~470px
// down the card.
// The profile slug IS the user's data (m-5ft10-176lb-6k-0610) — parse it
// client-side so the preview shows THEIR stats with zero extra plumbing.
// Legacy random tokens parse to nulls and the profile line is hidden.
function parseProfileSlug(token: string) {
  const parts = token.toLowerCase().split('-')
  const sex = parts[0] === 'm' ? 'Male' : parts[0] === 'f' ? 'Female' : null
  let height: string | null = null
  let weight: string | null = null
  let steps: string | null = null
  for (const p of parts.slice(1)) {
    const ft = p.match(/^(\d)ft(\d{1,2})$/)
    if (ft) height = `${ft[1]}'${ft[2]}"`
    const cm = p.match(/^(\d{2,3})cm$/)
    if (cm) height = `${cm[1]} cm`
    const lb = p.match(/^(\d{2,3})lb$/)
    if (lb) weight = `${lb[1]} lb`
    const kg = p.match(/^(\d{2,3})kg$/)
    if (kg) weight = `${kg[1]} kg`
    const k = p.match(/^(\d{1,2})k$/)
    if (k) steps = `~${k[1]}k steps/day`
  }
  return { sex, height, weight, steps }
}

function PlanImagePreview({
  tdeeRecommended,
  handoffToken,
  intent,
  isGlp1,
  honestPlan,
  lang = 'en',
}: {
  tdeeRecommended: number | null
  handoffToken: string
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  isGlp1?: boolean
  honestPlan?: HonestPlan | null
  lang?: Lang
}) {
  const t = getStrings(lang)
  // Faithful miniature of the TOP HALF of the REAL MMS plan card
  // (plan-card/templates/plan-card.html): warm light-pink card, Fraunces
  // brand wordmark in pink, the pink gradient rule, the user's own profile
  // one-liner, and the calorie hero — cropped just after the hero with a soft
  // fade that implies the fuller card below. UNBLURRED: because the number is
  // now visible it must be HONEST — the DEFICIT target band the card actually
  // delivers (lib/planTarget.ts), never the higher tdee_recommended. GLP-1
  // users get the numberless protein-floor hero their real card carries.
  const { sex, height, weight, steps } = parseProfileSlug(handoffToken)
  const profileLine = [sex, height, weight, steps].filter(Boolean).join(' · ')
  const planTitle = isGlp1
    ? t.cta.titleGlp1
    : intent === 'maintain'
      ? t.cta.titleMaintain
      : intent === 'gain'
        ? t.cta.titleGain
        : intent === 'recomp'
          ? t.cta.titleRecomp
          : t.cta.titleLose
  const hero = planTargetHero({
    tdeeRecommended,
    intent,
    sex: sex === 'Male' ? 'male' : sex === 'Female' ? 'female' : null,
    isGlp1,
    // Honest-rate floor: when the server sent honest numbers, the hero shows
    // the clamped floor target (adjusted) or the maintenance eating anchor
    // (activity-first) — never a band midpoint physics can't deliver.
    honestPlan,
    lang,
  })
  // Short date label, like the real card's brand-row date (e.g. "Jul 1").
  const dateLabel = new Date().toLocaleDateString(intlLocale(lang), {
    month: 'short',
    day: 'numeric',
  })
  return (
    <div className="cta-preview" aria-hidden="true">
      <div className="cta-preview-bubble">
        <span className="cta-preview-sender">{t.cta.previewSender}</span>
        <div className="cta-preview-card">
          {/* Brand row: Fraunces wordmark (the app's brand display face — the
              real card's Irish Grover isn't loaded here) + date, matching the
              real card's brand-row. */}
          <div className="cta-preview-brandrow">
            <span className="cta-preview-brand">NanoRhino</span>
            <span className="cta-preview-date">{dateLabel}</span>
          </div>
          <div className="cta-preview-rule" />
          <div className="cta-preview-title">{planTitle}</div>
          {profileLine && (
            <div className="cta-preview-stats">{profileLine}</div>
          )}
          <div className="cta-preview-hero">
            {hero.kind === 'glp1' ? (
              <>
                {/* GLP-1: the real card's hero is a protein floor, not a
                    deficit target — keep it numberless here too. */}
                <span className="cta-preview-hero-label">{t.cta.heroGlp1PlanLabel}</span>
                <span className="cta-preview-hero-alt">{t.cta.heroGlp1Alt}</span>
                <span className="cta-preview-hero-unit">{t.cta.heroGlp1Unit}</span>
              </>
            ) : hero.kind === 'none' ? (
              <>
                <span className="cta-preview-hero-label">{t.cta.heroNoneLabel}</span>
                <span className="cta-preview-hero-alt">{t.cta.heroNoneAlt}</span>
                <span className="cta-preview-hero-unit">{t.cta.heroNoneUnit}</span>
              </>
            ) : (
              <>
                <span className="cta-preview-hero-label">{hero.label}</span>
                <span className="cta-preview-hero-value">{hero.value}</span>
                <span className="cta-preview-hero-unit">{hero.unit}</span>
              </>
            )}
          </div>
          {/* Ghost of the plan tiles below the hero, dissolved by the card's
              bottom fade (::after) — the "there's more, it lands in your
              thread" crop. */}
          <div className="cta-preview-more">
            <div className="cta-preview-more-label">{t.cta.previewPlanLabel}</div>
            {/* Faint labels inside the ghost tiles so the cropped PLAN area
                reads as more-content-below, not unloaded skeleton boxes. */}
            <div className="cta-preview-more-tiles">
              <span>{t.cta.previewTileProtein}</span>
              <span>{t.cta.previewTileMeals}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// The coach thread: ONE chronological mock of the first week instead of the
// two stacked mocks this card used to carry (a plan-image preview above the
// button + a separate conversation demo below it, each with its own caption —
// together ~700px of scroll and three captions saying adjacent things).
// Order = what actually happens: the plan card lands about a minute after the
// claim text, meals get logged in plain words, and Sunday the target is
// re-measured. The Sunday beat is LOSE-ONLY (deficit-loop eligibility gate).
// Decorative — aria-hidden; the lede/body/fineprint carry the real copy.
//
// Lives BELOW the button on every surface: the deliverable is the reward for
// scrolling, not a toll gate in front of the tap.
function CoachThread({
  tdeeRecommended,
  handoffToken,
  intent,
  isGlp1,
  honestPlan,
  lang = 'en',
  photoDemo = false,
}: {
  tdeeRecommended: number | null
  handoffToken: string
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  isGlp1?: boolean
  honestPlan?: HonestPlan | null
  lang?: Lang
  // Claim-card redesign: the logged meal is a PHOTO (the calorie recognizer
  // is the card's first pitch). Legacy dark-ship cards keep the text demo.
  photoDemo?: boolean
}) {
  const t = getStrings(lang)
  const showWeek = (intent ?? 'lose') === 'lose' && !isGlp1
  return (
    <div className="cta-convo" aria-hidden="true">
      <p className="cta-convo-caption">
        {showWeek ? t.cta.convoCaptionWeek : t.cta.convoCaption}
      </p>
      <p className="cta-convo-meta cta-convo-meta-first">{t.cta.convoPlanMeta}</p>
      <PlanImagePreview
        tdeeRecommended={tdeeRecommended}
        handoffToken={handoffToken}
        intent={intent}
        isGlp1={isGlp1}
        honestPlan={honestPlan}
        lang={lang}
      />
      <div className="cta-convo-bubble cta-convo-out">
        {photoDemo ? t.cta.convoUserPhoto : t.cta.convoUser}
      </div>
      <div className="cta-convo-bubble cta-convo-in">
        {photoDemo ? t.cta.convoCoachPhoto : t.cta.convoCoach}
      </div>
      {showWeek && (
        <>
          <p className="cta-convo-meta">{t.cta.convoSundayMeta}</p>
          <div className="cta-convo-bubble cta-convo-in">{t.cta.convoCoachSunday}</div>
        </>
      )}
    </div>
  )
}

// US-only CTA surface: SMS link as the primary action, plus a toggleable
// "email it to me instead" path beneath. The toggle is collapsed by default
// so the SMS link stays the dominant call-to-action; users who can't / won't
// text us click "email" and the same EmailCaptureForm renders inline.
function UsCta({
  smsHref,
  handoffToken,
  conversationId,
  tdeeRecommended,
  intent,
  isGlp1,
  honestPlan,
  lang = 'en',
}: {
  smsHref: string
  handoffToken: string
  conversationId: string | null
  tdeeRecommended: number | null
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  isGlp1?: boolean
  honestPlan?: HonestPlan | null
  lang?: Lang
}) {
  const t = getStrings(lang)
  const [showEmail, setShowEmail] = useState(false)
  // Honest-rate floor, one-line disclosure (owner-approved): when the
  // requested/implied pace was clamped, the card says so — with the honest
  // date when a goal weight gave us one. Never shown for the GLP-1 surface
  // (numberless by design) or activity-first (its lede already reframes).
  const showHonestPace = !isGlp1 && honestPlan?.mode === 'adjusted'
  const honestDateLabel =
    showHonestPace && honestPlan?.goal_date
      ? new Date(`${honestPlan.goal_date}T00:00:00Z`).toLocaleDateString(intlLocale(lang), {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : null
  return (
    <>
      {showHonestPace && (
        <p className="cta-card-fineprint cta-card-honest-pace">
          {honestDateLabel ? t.cta.honestPaceWithDate(honestDateLabel) : t.cta.honestPaceNoDate}
        </p>
      )}
      <a
        className="cta-card-btn"
        href={smsHref}
        onClick={() => {
          recordCtaClick(handoffToken)
          // Ads funnel: any CTA conversion (SMS or email) = trial_started.
          trackAdsConversionOnce('trial_started')
        }}
      >
        {t.cta.textMeBtn}
      </a>
      <p className="cta-card-fineprint">{t.cta.fineprintUs}</p>
      <CoachThread
        tdeeRecommended={tdeeRecommended}
        handoffToken={handoffToken}
        intent={intent}
        isGlp1={isGlp1}
        honestPlan={honestPlan}
        lang={lang}
      />
      {showEmail ? (
        <div className="cta-card-fallback">
          <EmailCaptureForm conversationId={conversationId} source="us_fallback" lang={lang} />
        </div>
      ) : (
        <button
          type="button"
          className="cta-card-fallback-link"
          onClick={() => setShowEmail(true)}
        >
          {t.cta.emailInstead}
        </button>
      )}
    </>
  )
}

// US "live design" card (VITE_WHATSAPP_NUMBER set). Same shell as the legacy
// dark-ship US SMS card (UsCta) — kicker, cohort lede/body, no-app line,
// coach thread, returning-user UPDATE hint — with identical
// SMS-noun copy; the coaching body is channel-agnostic. The one structural
// difference from UsCta is the action stack (UsLiveCta): no email toggle, and a
// WhatsApp secondary text link + desktop WA QR beneath the primary SMS button.
// Kept a separate component (not a branch inside UsCta) so UsCta stays
// byte-identical for the number-unset dark-ship control + tests.
//
// 2026-07-30 flip: making WhatsApp the primary (2026-07-22..29) halved US
// paid-segment click-through (45% → 20%, z=2.43 p≈0.008), so SMS is the push
// again with WhatsApp a secondary link. Desktop still gets the WA QR — sms: has
// no desktop handler — an R3 experiment that runs on its own variable.
function UsLiveCard({
  waHref,
  smsHref,
  handoffToken,
  tdeeRecommended,
  tdeeLow,
  tdeeHigh,
  isGlp1,
  activityFirst,
  intent,
  honestPlan,
  showRehandoffHint,
  lang = 'en',
  forceDevice,
}: {
  waHref: string
  smsHref: string
  handoffToken: string
  tdeeRecommended: number | null
  tdeeLow?: number | null
  tdeeHigh?: number | null
  forceDevice?: 'phone' | 'desktop'
  isGlp1?: boolean
  activityFirst: boolean
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  honestPlan?: HonestPlan | null
  showRehandoffHint: boolean
  lang?: Lang
}) {
  return (
    <div className="cta-card cta-card-enter">
      <ClaimPitch
        handoffToken={handoffToken}
        isGlp1={isGlp1}
        activityFirst={activityFirst}
        intent={intent}
        lang={lang}
      />

      <UsLiveCta
        waHref={waHref}
        smsHref={smsHref}
        handoffToken={handoffToken}
        tdeeRecommended={tdeeRecommended}
        intent={intent}
        isGlp1={isGlp1}
        honestPlan={honestPlan}
        lang={lang}
        forceDevice={forceDevice}
      />

      {/* Returning-user hint: this conversation already clicked the SMS claim
          link on a prior calc, so it's bound to the SMS coach — point it at the
          SMS UPDATE flow to refresh the existing coach. US branch only;
          first-timers never see it. */}
      {showRehandoffHint &&
        (lang === 'es' ? (
          <p className="cta-card-rehandoff">
            ¿Ya le escribes a tu coach de <strong>NanoRhino</strong>? Envía{' '}
            <strong>UPDATE</strong> al (915) 277-7888 para actualizar tu plan con
            estos números.
          </p>
        ) : (
          <p className="cta-card-rehandoff">
            Already texting your <strong>NanoRhino</strong> coach? Text{' '}
            <strong>UPDATE</strong> to (915) 277-7888 to refresh your plan with
            these numbers.
          </p>
        ))}
    </div>
  )
}

// Action stack for the US live card: SMS as the primary claim button (filled
// .cta-card-btn) with the "what tapping does" hint under it, WhatsApp demoted to
// a secondary text link beneath. NO email on this surface (removed 2026-07-22).
// Above the button: only the honest-pace line (when the pace was clamped).
// Below it: hint, WhatsApp link, fineprint, then the coach thread. Lede +
// fineprint (t.cta.fineprintUs) are uniform SMS-noun on both devices.
//
// DESKTOP (R3): unchanged by the 2026-07-30 SMS flip. sms: has no handler on a
// laptop, so the primary button would be a dead end there; desktop instead gets
// the WhatsApp QR (the one path that moves the claim onto a phone) + a WA
// desktop text link. Desktop went 56 impressions -> 6 clicks -> 0 redemptions
// across BOTH CTA eras, so its QR experiment runs on its own variable.
function UsLiveCta({
  waHref,
  smsHref,
  handoffToken,
  tdeeRecommended,
  intent,
  isGlp1,
  honestPlan,
  lang = 'en',
  forceDevice,
}: {
  waHref: string
  smsHref: string
  handoffToken: string
  tdeeRecommended: number | null
  intent?: 'lose' | 'maintain' | 'recomp' | 'gain'
  isGlp1?: boolean
  honestPlan?: HonestPlan | null
  lang?: Lang
  forceDevice?: 'phone' | 'desktop'
}) {
  const t = getStrings(lang)
  const phoneLikeAuto = usePhoneLikeDevice()
  const phoneLike = forceDevice ? forceDevice === 'phone' : phoneLikeAuto
  const showHonestPace = !isGlp1 && honestPlan?.mode === 'adjusted'
  const honestDateLabel =
    showHonestPace && honestPlan?.goal_date
      ? new Date(`${honestPlan.goal_date}T00:00:00Z`).toLocaleDateString(intlLocale(lang), {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : null
  return (
    <>
      {showHonestPace && (
        <p className="cta-card-fineprint cta-card-honest-pace">
          {honestDateLabel ? t.cta.honestPaceWithDate(honestDateLabel) : t.cta.honestPaceNoDate}
        </p>
      )}
      {/* DESKTOP (R3): a button is worthless here — sms: has no handler and
          wa.me routes to WhatsApp Web, which demands a phone-scan anyway.
          Desktop went 56 impressions -> 6 clicks -> 0 redemptions across BOTH
          CTA eras. The QR moves the request to the device that can finish it.
          Left EXACTLY as the WhatsApp era: the SMS-primary flip does not touch
          this branch (one variable at a time). */}
      {!phoneLike ? (
        <>
          <WhatsAppQr href={waHref} caption={t.cta.qrCaption} />
          {/* Kept for the minority who really do run WhatsApp Desktop — a text
              link, not a button, so it cannot compete with the QR. */}
          <p className="cta-card-fineprint cta-card-mechanic">
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                recordCtaClick(handoffToken, 'whatsapp')
                trackAdsConversionOnce('trial_started')
              }}
            >
              {t.cta.qrDesktopLink}
            </a>
          </p>
        </>
      ) : (
        /* Primary and ONLY action on US mobile since 2026-07-31: SMS. Tap →
           sms_link_clicked_at (default channel) — the same beacon +
           ads-funnel calls the legacy US SMS button fires.
           The WhatsApp "message us there instead" text link and the
           "opens your Messages app…" hint under the button were both removed
           (owner call) — one action, no supporting prose. wa_link_clicked_at
           therefore only records desktop QR-page taps and the intl surface
           from here on; a US mobile visitor who wants WhatsApp has no entry. */
        <a
          className="cta-card-btn"
          href={smsHref}
          onClick={() => {
            recordCtaClick(handoffToken)
            trackAdsConversionOnce('trial_started')
          }}
        >
          {t.cta.claimBtn}
        </a>
      )}
      <ClaimFineprint showResults={showResultsClaim({ intent, isGlp1, honestPlan })} lang={lang} />
      <CoachThread
        tdeeRecommended={tdeeRecommended}
        handoffToken={handoffToken}
        intent={intent}
        isGlp1={isGlp1}
        honestPlan={honestPlan}
        lang={lang}
        photoDemo
      />
    </>
  )
}

function EmailCaptureForm({
  conversationId,
  source,
  lang = 'en',
}: {
  conversationId: string | null
  source: EmailCaptureSource
  lang?: Lang
}) {
  const t = getStrings(lang)
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<EmailStatus>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!conversationId) {
      setErrorMsg(t.cta.email.errNoContext)
      setStatus('error')
      return
    }
    const trimmed = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg(t.cta.email.errInvalid)
      setStatus('error')
      return
    }
    setStatus('submitting')
    setErrorMsg(null)
    try {
      await submitPlanEmail(conversationId, trimmed, source)
      // Ads funnel: successful submit only — failures aren't conversions.
      // Covers both sources (intl_form + us_fallback).
      trackAdsConversionOnce('trial_started')
      setStatus('submitted')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t.cta.email.errFailed)
      setStatus('error')
    }
  }

  if (status === 'submitted') {
    return (
      <div className="cta-card-confirm">
        <p className="cta-card-confirm-headline">{t.cta.email.confirmHeadline}</p>
        {lang === 'es' ? (
          <p className="cta-card-body">
            Te enviaremos una copia de este plan por correo en un día.{' '}
            <strong>Responde a ese correo cuando quieras</strong> para chatear con
            tu coach de nutrición con IA — hacer preguntas, ajustar el plan o
            reportarte en un día difícil.
          </p>
        ) : (
          <p className="cta-card-body">
            We'll email you a copy of this plan within a day. <strong>Reply to
            that email anytime</strong> to chat with your AI nutrition coach —
            ask questions, adjust the plan, or check in on a hard day.
          </p>
        )}
      </div>
    )
  }

  return (
    <form className="cta-card-form" onSubmit={onSubmit}>
      {lang === 'es' ? (
        <p className="cta-card-body" style={{ marginBottom: 4 }}>
          Deja tu correo y te enviamos este plan. <strong>Responde a ese correo
          cuando quieras</strong> para chatear con el coach de nutrición con IA —
          ajustar el plan, hacer preguntas, destrabarte.
        </p>
      ) : (
        <p className="cta-card-body" style={{ marginBottom: 4 }}>
          Drop your email and we'll send you this plan. <strong>Reply to that
          email anytime</strong> to chat with the AI nutrition coach — adjust
          the plan, ask questions, get unstuck.
        </p>
      )}
      <div className="cta-card-form-row">
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          placeholder={t.cta.email.placeholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'submitting'}
          required
        />
        <button
          type="submit"
          className="cta-card-btn"
          disabled={status === 'submitting' || !email.trim()}
        >
          {status === 'submitting' ? t.cta.email.sending : t.cta.email.submit}
        </button>
      </div>
      {errorMsg && (
        <p className="cta-card-form-error" role="alert">
          {errorMsg}
        </p>
      )}
      <p className="cta-card-fineprint">{t.cta.email.fineprint}</p>
    </form>
  )
}
